import { Edit2, Trash2, Search } from "lucide-react";
import { Product } from "../../types/product";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onRowClick?: (product: Product) => void;
  isLoading?: boolean;
}

export const ProductTable = ({
  products,
  onEdit,
  onDelete,
  onRowClick,
  isLoading = false,
}: ProductTableProps) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
              <th className="col-span-4 text-left font-bold">Product</th>
              <th className="col-span-2 text-left font-bold">SKU</th>
              <th className="col-span-2 text-left font-bold">Barcode</th>
              <th className="col-span-2 text-left font-bold">Price</th>
              <th className="col-span-2 text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                  </div>
                </td>
              </tr>
            ) : products.length === 0 ? (
              <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
                <td colSpan={12} className="w-full h-full flex flex-col items-center justify-center lg:table-cell">
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <Search className="w-12 h-12 mb-3 opacity-20" />
                    <p className="text-lg font-medium text-gray-500">
                      No products found
                    </p>
                    <p className="text-sm">Add your first product to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full cursor-pointer relative"
                    onClick={() => onRowClick?.(product)}
                  >
                    {/* Product Info */}
                    <td className="lg:col-span-4 w-full lg:w-auto">
                      <div className="flex items-center gap-3">
                        <img
                          src={product.image || "/placeholder.png"}
                          alt={product.name}
                          className="w-14 h-14 lg:w-12 lg:h-12 rounded-lg object-cover flex-shrink-0 border border-gray-100"
                        />
                        <div className="min-w-0">
                          <p className="font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                            {product.name}
                          </p>
                          <p className="lg:hidden text-xs text-gray-500 truncate mt-0.5">
                            {product.sku ? `SKU: ${product.sku}` : "No SKU"}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* SKU (Hidden on mobile as it's under title) */}
                    <td className="hidden lg:col-span-2 text-gray-700 truncate lg:table-cell">
                      {product.sku || "—"}
                    </td>

                    {/* Barcode */}
                    <td className="lg:col-span-2 text-gray-700 truncate w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Barcode</span>
                      <span className="font-medium lg:font-normal">{product.barcode || "—"}</span>
                    </td>

                    {/* Price */}
                    <td className="lg:col-span-2 font-bold lg:font-medium text-[#885ABB] lg:text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Price</span>
                      <span>{currencySymbol}{product.price.toFixed(2)}</span>
                    </td>

                    {/* Actions */}
                    <td className="lg:col-span-2 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(product);
                        }}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                        title="Edit product"
                      >
                        <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(product.id);
                        }}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                        title="Delete product"
                      >
                        <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
